import { useEffect, useState } from "react";
import { FilterMultiSelectContent } from "@/components/filters/FilterMultiSelectContent";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULT_LIMIT = 50;

interface OpportunityCompanyFilterContentProps {
  selected: string[];
  onChange: (next: string[]) => void;
}

/**
 * Dropdown body for the Company filter: a server-searched multi-select of the
 * companies attached to opportunities the viewer can see, with the current
 * selections pinned above the results.
 * @param selected - Currently selected company ids
 * @param onChange - Called with the next selected company ids
 * @returns The company-filter dropdown content
 */
export function OpportunityCompanyFilterContent({
  selected,
  onChange,
}: OpportunityCompanyFilterContentProps) {
  const companies = useCompaniesStore((state) => state.companies);
  const fetchCompanyFilterOptions = useOpportunitiesStore(
    (state) => state.fetchOpportunityCompanyFilterOptions,
  );
  const optionsLoading = useOpportunitiesStore(
    (state) => state.companyFilterOptionsLoading,
  );
  const [search, setSearch] = useState("");
  const [resultIds, setResultIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      fetchCompanyFilterOptions({
        search: search || undefined,
        limit: SEARCH_RESULT_LIMIT,
      }).then((ids) => {
        if (!cancelled) {
          setResultIds(ids);
        }
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [search, fetchCompanyFilterOptions]);

  // Pin selections above the results so they stay visible while searching.
  const items = [
    ...selected,
    ...resultIds.filter((id) => !selected.includes(id)),
  ].flatMap((id) => {
    const company = companies.get(id);
    return company ? [{ id, name: company.name }] : [];
  });

  return (
    <FilterMultiSelectContent
      items={items}
      selected={selected}
      onChange={onChange}
      placeholder="Search companies..."
      emptyText="No companies found."
      search={{ value: search, onChange: setSearch, loading: optionsLoading }}
    />
  );
}
